import { externalContractErrorMessage } from "utils/external-contract-errors";

/** `t` from useTranslation, typed loosely as the other error helpers do. */
type TFunction = (key: string, opts?: any) => string;

/**
 * Codes the fuel report endpoints answer with: `files/build-pdf` (creating the
 * report's document) and `fuelreport/patch` (editing the report). Kept apart
 * from `external-contract-errors.ts`, which is about orders.
 */
const CODE_KEYS: Record<string, string> = {
  FUEL_REPORT_NOT_FOUND: "fuel_report_not_found",
  FUEL_REPORT_ACCESS_DENIED: "forbidden",
  // fuelreport/patch: an employee editing a report that is already signed
  FUEL_REPORT_SIGNED: "fuel_report_signed_edit_forbidden",
  // fuelreport/patch: another save or document build of this report is still
  // running. (For `files/build-pdf` the same code counts as "already created",
  // see `isFuelReportDocumentAlreadyCreated`.)
  FUEL_REPORT_DOCUMENT_IN_PROGRESS: "fuel_report_document_in_progress",
};

/** `files/build-pdf` refuses to build a second document for one report. */
const DOCUMENT_ALREADY_CREATED_CODES = [
  "FUEL_REPORT_DOCUMENT_EXISTS",
  "FUEL_REPORT_DOCUMENT_IN_PROGRESS",
];

const responseOf = (error: any) => error?.response;

/**
 * True when the server says the report already has a document, or another
 * save for it is still running — the document is there (or about to be), so
 * the form treats it as done instead of showing an error.
 */
export function isFuelReportDocumentAlreadyCreated(error: any): boolean {
  const response = responseOf(error);
  if (response?.status !== 409) return false;
  const code = response?.data?.code || response?.data?.error;
  return DOCUMENT_ALREADY_CREATED_CODES.includes(code);
}

/** The id of the report's existing document, when the 409 carries it. */
export function existingFuelReportDocumentId(error: any): string | null {
  const fileId = responseOf(error)?.data?.fileId;
  return typeof fileId === "string" && fileId ? fileId : null;
}

/**
 * The display-ready text for a failed fuel report request (not an i18n key:
 * do not pass it through `t()` again).
 */
export function fuelReportErrorMessage(error: any, t: TFunction): string {
  const response = responseOf(error);
  const data = response?.data;
  const code = data?.code || data?.error;
  if (code && CODE_KEYS[code]) return t(CODE_KEYS[code]);
  // fuelreport/patch: the signing lock refuses to drop a signer who can no
  // longer sign (the backend sends the key as `message`).
  if (data?.message === "template_signatures_locked") {
    return t("fuel_report_signatures_locked");
  }
  // Validation refusals ("Asset not found", "Missing required fields ...")
  // come as `{ error: "<English sentence>" }` without a code; the sentence is
  // specific, so it reads better than a generic fallback. A bare code-like
  // string (no spaces) is not worth showing.
  if (
    response?.status === 400 &&
    !data?.message &&
    typeof data?.error === "string" &&
    /\s/.test(data.error)
  ) {
    return data.error;
  }
  // A bare 404 on these endpoints can only mean the report is gone; the
  // order-oriented wording of the fallback below would mislead here.
  if (response?.status === 404) return t("fuel_report_not_found");
  return externalContractErrorMessage(error, t);
}
